import {createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {usePaginaImagen} from '@/hooks/bateria/use-pagina-imagen'
import {useRecorteAlVuelo} from '@/hooks/bateria/use-recorte-al-vuelo'
import {usePrecargaCortes} from '@/components/bateria/cola/use-precarga-cortes'
import {usePrecargaRecortes} from '@/components/bateria/cola/use-precarga-recortes'
import {claveDeRef, paqueteDeRef, type TarjetaCola} from '@/lib/bateria-cola'
import {tarjetaContestable} from '@/lib/cola-estados'
import {marcarRecorteCaido, useRecorteVivo} from '@/lib/recortes-caidos'
import {cn} from '@/lib/utils'

/**
 * Interlock: sin papel pintado no se decide. `puedeDecidir` es la única vara, para teclado y botones;
 * navegar, `D` y `Esc` siguen vivos siempre.
 */
export type EvidenciaDeTarjeta =
  | {estado: 'en_curso'}
  | {estado: 'imagen'; url: string}
  /** Final: bloquear aquí dejaría sin salida a quien resuelve con el cuadernillo al lado. */
  | {estado: 'sin_imagen'}

type Peldano = {cargando: boolean; url: string | null}

/**
 * La misma escalera que pinta la tarjeta. `recorte` es el vivo (`useRecorteVivo`): el campo crudo
 * dejaría al interlock esperando un recorte que el lifecycle borró.
 */
export function evidenciaDeTarjeta(
  tarjeta: TarjetaCola | undefined,
  recorte: string | null,
  corte: Peldano,
  hoja: Peldano
): EvidenciaDeTarjeta {
  if (!tarjeta) return {estado: 'sin_imagen'}
  if (recorte) return {estado: 'imagen', url: recorte}
  if (tarjeta.tipo === 'ubicar') return {estado: 'sin_imagen'}
  if (tarjeta.tipo === 'marca') {
    if (corte.cargando) return {estado: 'en_curso'}
    if (corte.url !== null) return {estado: 'imagen', url: corte.url}
  }
  if (hoja.cargando) return {estado: 'en_curso'}
  if (hoja.url !== null) return {estado: 'imagen', url: hoja.url}
  return {estado: 'sin_imagen'}
}

type Interlock = {
  marcarPintada: (url: string) => void
  /** Intentos de decidir sin papel: cada cambio re-anima la espera visible, sin toast. */
  pulso: number
  /** Hay una navegación retenida esperando el papel del destino (`use-transito-cola.ts`). */
  enTransito: boolean
}

const InterlockContext = createContext<Interlock | null>(null)

/**
 * Todo recorte se ve fijo 400 ms antes de que una decisión actúe: la ráfaga queda en ~2,5 tarjetas/s.
 * Navegar no se frena.
 */
const FIJACION_MINIMA_MS = 400

/** El reloj arranca cuando la tarjeta tiene su evidencia pintada, no cuando llega. */
export function useFijacionMinima(ref: string, lista: boolean): boolean {
  const [enFijacion, setEnFijacion] = useState(true)

  useEffect(() => {
    setEnFijacion(true)
    if (!lista) return
    const id = setTimeout(() => setEnFijacion(false), FIJACION_MINIMA_MS)
    return () => clearTimeout(id)
  }, [ref, lista])

  return enFijacion
}

/**
 * Decodifica fuera del DOM porque el destino aún no está montado; un `decode()` rechazado cuenta
 * como listo, igual que un `<img>` con `onError`: esperarlo trabaría la cola.
 */
export function useImagenDecodificada(url: string | null): boolean {
  const [decodificada, setDecodificada] = useState<string | null>(null)

  useEffect(() => {
    if (!url) return
    let vivo = true
    const imagen = new Image()
    imagen.src = url
    const marcar = () => {
      if (vivo) setDecodificada(url)
    }
    const espera = typeof imagen.decode === 'function' ? imagen.decode() : Promise.resolve()
    // El rechazo se anota antes de marcar lista: React aplica ambas en un render y el que vería «lista»
    // ya ve la evidencia en el peldaño siguiente, así que no transiciona.
    espera.then(marcar, () => {
      marcarRecorteCaido(url)
      marcar()
    })
    return () => {
      vivo = false
    }
  }, [url])

  return !url || decodificada === url
}

/** Usa las mismas claves de consulta que la tarjeta: observarlas de antemano no cuesta peticiones. */
export function useEvidenciaDeUnaTarjeta(
  businessId: string,
  evaluacionId: string,
  tarjeta: TarjetaCola | undefined
): EvidenciaDeTarjeta {
  const ref = tarjeta?.ref ?? ''
  const paquete = paqueteDeRef(ref)
  // El recorte vivo: uno que el lifecycle borró tiene que encender el corte al vuelo.
  const recorte = useRecorteVivo(tarjeta?.crop_download_url)
  // Mismas condiciones que `recorteDeLaFila` (`tarjeta-marca.tsx`) y `Papel` (`tarjeta-campo.tsx`):
  // de más rasterizaría PDFs de sobra; de menos, el interlock no vería la espera.
  const esCorte = tarjeta?.tipo === 'marca' && recorte === null
  const corte = useRecorteAlVuelo(businessId, evaluacionId, paquete, claveDeRef(ref), esCorte)
  const peldanoCorte: Peldano = {cargando: corte.isLoading, url: corte.data?.url ?? null}
  const hojaHaceFalta =
    tarjeta !== undefined &&
    tarjeta.tipo !== 'ubicar' &&
    recorte === null &&
    (!esCorte || (!peldanoCorte.cargando && peldanoCorte.url === null))
  const hoja = usePaginaImagen(
    businessId,
    evaluacionId,
    hojaHaceFalta ? paquete : null,
    hojaHaceFalta ? tarjeta.pagina_numero : null
  )
  return evidenciaDeTarjeta(tarjeta, recorte, peldanoCorte, {
    cargando: hoja.isLoading,
    url: hoja.data?.url ?? null,
  })
}

/** Se llama una vez, en la pantalla. */
export function useEvidenciaDeLaCola({
  businessId,
  evaluacionId,
  actual,
  deLaSeccion,
  indice,
  enTransito,
}: {
  businessId: string
  evaluacionId: string
  actual: TarjetaCola | undefined
  deLaSeccion: TarjetaCola[]
  indice: number
  /** Apaga decidir: la decisión que disparó el tránsito ya se registró. */
  enTransito: boolean
}) {
  const ref = actual?.ref ?? ''
  const evidencia = useEvidenciaDeUnaTarjeta(businessId, evaluacionId, actual)

  // Un solo casillero: en cada instante solo un componente pinta la evidencia de la tarjeta en curso.
  const [pintada, setPintada] = useState<string | null>(null)
  const [pulso, setPulso] = useState(0)
  const marcarPintada = useCallback((url: string) => setPintada((previa) => (previa === url ? previa : url)), [])
  const pulsar = useCallback(() => setPulso((n) => n + 1), [])
  const contexto = useMemo<Interlock>(() => ({marcarPintada, pulso, enTransito}), [marcarPintada, pulso, enTransito])

  // `calentar` pasa a `usePrecargaCortes` para que un corte ya resuelto entre a la misma cola de descarga+decode.
  const calentarBytes = usePrecargaRecortes(deLaSeccion, indice)
  usePrecargaCortes(businessId, evaluacionId, deLaSeccion, indice, calentarBytes)

  const lista = evidencia.estado === 'imagen' ? pintada === evidencia.url : evidencia.estado === 'sin_imagen'
  const enFijacion = useFijacionMinima(ref, lista)
  return {
    puedeDecidir: tarjetaContestable(actual) && lista && !enFijacion && !enTransito,
    evidencia,
    pulsar,
    contexto,
    /** Sin papel la pulsación pulsa el aviso; con papel (fijación o tránsito) se traga en silencio. */
    evidenciaALaVista: lista,
  }
}

/** Fuera del proveedor (ficha suelta, pruebas) el contexto es `null` y todo es inerte. */
export function EvidenciaDeLaCola({valor, children}: {valor: Interlock; children: ReactNode}) {
  return <InterlockContext.Provider value={valor}>{children}</InterlockContext.Provider>
}

/** Un `<img>` que falla también es final: esperarlo trabaría la cola. */
export function useEvidenciaPintada(url: string, listo: boolean): void {
  const marcar = useContext(InterlockContext)?.marcarPintada
  useEffect(() => {
    if (listo && url !== '') marcar?.(url)
  }, [listo, url, marcar])
}

export function usePulsoDeEspera(): number {
  return useContext(InterlockContext)?.pulso ?? 0
}

function useEnTransito(): boolean {
  return useContext(InterlockContext)?.enTransito ?? false
}

/** Encima del recorte y no en su lugar: la visual nunca abandona un recorte. Se ancla a una caja `relative`. */
export function IndicadorDeTransito() {
  const {t} = useTranslation()
  const enTransito = useEnTransito()
  if (!enTransito) return null
  return (
    <span
      role="status"
      data-testid="cola-cargando-siguiente"
      className="pointer-events-none absolute top-1 right-1 z-20 flex items-center gap-1.5 rounded-md bg-card/90 px-2 py-1 font-mono text-[11px] text-muted-foreground shadow-sm">
      <span aria-hidden="true" className="size-2.5 animate-spin rounded-full border border-border border-t-primary" />
      {t('admin.bateria.cola.papel.cargandoSiguiente')}
    </span>
  )
}

const AVISO_ESPERA = 'rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground'

/** El `key` reinicia la animación en cada intento; sin él la segunda pulsación no se vería. */
export function EsperandoElPapel({testId, children}: {testId: string; children: ReactNode}) {
  const pulso = usePulsoDeEspera()
  return (
    <p key={pulso} data-testid={testId} className={cn(AVISO_ESPERA, pulso > 0 && 'pulso-espera')}>
      {children}
    </p>
  )
}
